import { useCallback } from 'react'
import { Input, TextArea, PriceInput } from '../../shared/components'
import Button from '../../shared/components/Button'
import { DropdownProvider } from '../../shared/utils/DropdownContext'
import { useAddProduct } from './hooks/useAddProduct'
import { usePropertyLogic } from './hooks/usePropertyLogic'
import { PropertiesModal } from '../../features/products/modals/PropertiesModal'
import { CategorySelect } from '../../features/products/components/CategorySelect'
import { SizeGuideSelect } from '../../features/products/components/SizeGuideSelect'
import { ProductMediaGallery } from '../../features/products/components/ProductMediaGallery'
import '../../styles/animations.css'

export const AddProductPage = () => {
  const {
    formData, setFormData,
    propertyData, setPropertyData,
    selectedSize, setSelectedSize,
    selectedMaterial, setSelectedMaterial,
    selectedColor, setSelectedColor,
    showPropertiesModal, setShowPropertiesModal,
    currentStep, setCurrentStep,
    categories, apiColors, sizeGuides, materials,
    isBaseDataValid, canAddVariants, isDraftLoading, isPublishLoading,
    handleSaveDraft, handlePublish, navigate
  } = useAddProduct()

  const {
    getAvailableSizes, getAvailableMaterials, getAvailableColors,
    getSizeCostPrice, getSizePrice, getMaterialCostPrice, getMaterialPrice, getColorCostPrice, getColorPrice, hasMaterialPrices
  } = usePropertyLogic(
    propertyData, selectedSize, selectedMaterial, selectedColor,
    setCurrentStep, showPropertiesModal
  )

  const addProperty = () => {
    if (!canAddVariants()) return
    setShowPropertiesModal(true)
    setCurrentStep(0)
  }


  const colorsToUse = apiColors.length > 0 ? apiColors :[]
  const colorOptions = colorsToUse.map(color => ({ name: color.name, value: color.hexValue }))

  const getCurrentQuantity = () => {
    if (selectedSize && selectedMaterial && selectedColor) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      const materialData = sizeData?.materials.find(m => m.name === selectedMaterial)
      const colorData = materialData?.colors.find(c => c.hex === selectedColor)
      return colorData?.quantity || 0
    }
    return formData.quantity || 0
  }

  const getCurrentImages = () => {
    if (selectedSize && selectedMaterial && selectedColor) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      const materialData = sizeData?.materials.find(m => m.name === selectedMaterial)
      const colorData = materialData?.colors.find(c => c.hex === selectedColor)
      return [...(colorData?.imageUrls || []), ...(colorData?.images || [])]
    }
    return formData.images
  }

  const getCurrentWeight = () => {
    if (selectedSize && selectedMaterial && selectedColor) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      const materialData = sizeData?.materials.find(m => m.name === selectedMaterial)
      const colorData = materialData?.colors.find(c => c.hex === selectedColor)
      return colorData?.weight || 0
    }
    return 0
  }

  const handleSizeCostPriceChange = (value: string) => {
    const additionalCostPrice = parseInt(value) || 0
    if (selectedSize) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === selectedSize ? { ...s, additionalCostPrice } : s
        )
      }))
    }
  }

  const handleSizePriceChange = (value: string) => {
    const price = parseInt(value) || 0
    if (selectedSize) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === selectedSize ? { ...s, price } : s
        )
      }))
      if (!hasMaterialPrices()) {
        setCurrentStep(1)
      }
    }
  }

  const handleMaterialCostPriceChange = (value: string) => {
    const additionalCostPrice = parseInt(value) || 0
    if (selectedSize && selectedMaterial) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === selectedSize
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === selectedMaterial ? { ...m, additionalCostPrice } : m
                )
              }
            : s
        )
      }))
    }
  }

  const handleMaterialPriceChange = (value: string) => {
    const price = parseInt(value) || 0
    if (selectedSize && selectedMaterial) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === selectedSize
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === selectedMaterial ? { ...m, price } : m
                )
              }
            : s
        )
      }))
      setCurrentStep(2)
    }
  }

  const handleColorCostPriceChange = (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => {
    const additionalCostPrice = parseInt(value) || 0
    const sizeToUse = targetSize || selectedSize
    const materialToUse = targetMaterial || selectedMaterial
    const colorToUse = targetColor || selectedColor
    
    if (sizeToUse && materialToUse && colorToUse) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === sizeToUse
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === materialToUse
                    ? {
                        ...m,
                        colors: m.colors.map(c => 
                          c.hex === colorToUse ? { ...c, additionalCostPrice } : c
                        )
                      }
                    : m
                )
              }
            : s
        )
      }))
    }
  }

  const handleColorPriceChange = (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => {
    const price = parseInt(value) || 0
    const sizeToUse = targetSize || selectedSize
    const materialToUse = targetMaterial || selectedMaterial
    const colorToUse = targetColor || selectedColor
    
    if (sizeToUse && materialToUse && colorToUse) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === sizeToUse
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === materialToUse
                    ? {
                        ...m,
                        colors: m.colors.map(c => 
                          c.hex === colorToUse ? { ...c, price } : c
                        )
                      }
                    : m
                )
              }
            : s
        )
      }))
    }
  }

  const handleQuantityChange = (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => {
    const quantity = parseInt(value) || 0
    const sizeToUse = targetSize || selectedSize
    const materialToUse = targetMaterial || selectedMaterial
    const colorToUse = targetColor || selectedColor
    
    if (sizeToUse && materialToUse && colorToUse) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === sizeToUse
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === materialToUse
                    ? {
                        ...m,
                        colors: m.colors.map(c => 
                          c.hex === colorToUse ? { ...c, quantity } : c
                        )
                      }
                    : m
                )
              }
            : s
        )
      }))
    } else {
      setFormData({ ...formData, quantity })
    }
  }

  const handleWeightChange = (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => {
    const weight = parseFloat(value) || 0
    const sizeToUse = targetSize || selectedSize
    const materialToUse = targetMaterial || selectedMaterial
    const colorToUse = targetColor || selectedColor
    
    if (sizeToUse && materialToUse && colorToUse) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === sizeToUse
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === materialToUse
                    ? {
                        ...m,
                        colors: m.colors.map(c => 
                          c.hex === colorToUse ? { ...c, weight } : c
                        )
                      }
                    : m
                )
              }
            : s
        )
      }))
    }
  }

  const handleImageChange = (files: File[], targetSize?: string, targetMaterial?: string, targetColor?: string) => {
    const sizeToUse = targetSize || selectedSize
    const materialToUse = targetMaterial || selectedMaterial
    const colorToUse = targetColor || selectedColor
    
    if (sizeToUse && materialToUse && colorToUse) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === sizeToUse)
      const materialData = sizeData?.materials.find(m => m.name === materialToUse)
      const colorData = materialData?.colors.find(c => c.hex === colorToUse)
      const remainingSlots = 3 - (colorData?.images.length || 0)
      const newImages = files.slice(0, remainingSlots)
      
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === sizeToUse
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === materialToUse
                    ? {
                        ...m,
                        colors: m.colors.map(c => 
                          c.hex === colorToUse 
                            ? { ...c, images: [...c.images, ...newImages] }
                            : c
                        )
                      }
                    : m
                )
              }
            : s
        )
      }))
    } else {
      const remainingSlots = 3 - formData.images.length
      const newImages = files.slice(0, remainingSlots)
      setFormData({ ...formData, images: [...formData.images, ...newImages] })
    }
  }

  const handleImageDelete = (index: number, targetSize?: string, targetMaterial?: string, targetColor?: string) => {
    const sizeToUse = targetSize || selectedSize
    const materialToUse = targetMaterial || selectedMaterial
    const colorToUse = targetColor || selectedColor
    
    if (sizeToUse && materialToUse && colorToUse) {
      setPropertyData(prev => ({
        ...prev,
        sizes: prev.sizes.map(s => 
          s.sizeId === sizeToUse
            ? {
                ...s,
                materials: s.materials.map(m => 
                  m.name === materialToUse
                    ? {
                        ...m,
                        colors: m.colors.map(c => 
                          c.hex === colorToUse 
                            ? { ...c, images: c.images.filter((_, i) => i !== index) }
                            : c
                        )
                      }
                    : m
                )
              }
            : s
        )
      }))
    } else {
      const newImages = formData.images.filter((_, i) => i !== index)
      setFormData({ ...formData, images: newImages })
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePropertiesModal = useCallback(() => setShowPropertiesModal(!showPropertiesModal), [showPropertiesModal])

  // const newLocal = <div className="space-y-6">
  //   <PriceInput
  //     label="Base price (₦)"
  //     placeholder="0.00"
  //     value={formData.price}
  //     className='h-[38px]'
  //     onChange={(kobo) => setFormData({ ...formData, price: kobo.toString() })} />

  //   {/* <Select
  //     label="Style guide"
  //     options={sizeGuides?.map((sizeGuide) => ({ title: sizeGuide.name, value: sizeGuide?.id })) ?? []}
  //     value={formData.styleGuide}
  //     className='h-[38px]'
  //     onChange={(value) => setFormData({ ...formData, styleGuide: value })}
  //     placeholder="Select size guide" /> */}
  // </div>
  return (
    <DropdownProvider>
      <div className="py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => navigate('/products')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleSaveDraft}
              disabled={!isBaseDataValid() || isDraftLoading || isPublishLoading}
              loading={isDraftLoading}
            >
              Save as Draft
            </Button>
            <Button variant="outline" onClick={() => navigate('/products')} disabled={isDraftLoading || isPublishLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handlePublish}
              disabled={!isBaseDataValid() || isDraftLoading || isPublishLoading}
              loading={isPublishLoading}
            >
              Publish
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6 border-[1px] border-[#D9D9D9] rounded-[25px] p-7">
          <div className="grid grid-cols-3 gap-8">
            <Input
              label="Product name"
              placeholder="Product name"
              value={formData.name}
              className='h-[38px]'
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />

            <PriceInput
              label="Base cost price (₦)"
              placeholder="0.00"
              value={formData.baseCostPrice}
              className='h-[38px]'
              onChange={(kobo) => setFormData({ ...formData, baseCostPrice: kobo.toString() })}
            />

            <PriceInput
              label="Base price (₦)"
              placeholder="0.00"
              value={formData.price}
              className='h-[38px]'
              onChange={(kobo) => setFormData({ ...formData, price: kobo.toString() })}
            />
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div>
              <Input
                label="Discount (%)"
                type="number"
                placeholder="0"
                min={0}
                max={100}
                value={formData.discountPercentage.toString()}
                className='h-[38px]'
                onChange={(e) => {
                  const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                  setFormData({ ...formData, discountPercentage: val })
                }}
              />
              {formData.discountPercentage > 0 && formData.price && (
                <p className="text-xs text-muted-foreground mt-1">
                  Discounted: ₦{((parseInt(formData.price) || 0) / 100 * (1 - formData.discountPercentage / 100)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
            </div>
            <div /> {/* spacer */}
            <div /> {/* spacer */}
          </div>

          <div className="grid grid-cols-3 gap-8">
            <CategorySelect
              label="Category"
              options={categories.map(cat => ({ title: cat.name, value: cat.id }))}
              value={formData.category}
              className='h-[38px]'
              onChange={(value) => setFormData({ ...formData, category: value })}
              placeholder="Select category"
            />

            <SizeGuideSelect
              label="Size guide"
              options={sizeGuides?.map((sizeGuide) => ({ title: sizeGuide.name, value: sizeGuide?.id })) ?? []}
              value={formData.styleGuide}
              className='h-[38px]'
              onChange={(value) => setFormData({ ...formData, styleGuide: value })}
              placeholder="Select size guide"
            />

            <div className="relative">
              <Input
                label="Product duration"
                type="number"
                placeholder="7"
                min={1}
                value={formData.productionTimeInDays}
                className='h-[38px] pr-12'
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0
                  if (value >= 1) {
                    setFormData({ ...formData, productionTimeInDays: value.toString() })
                  } else if (e.target.value === '') {
                    setFormData({ ...formData, productionTimeInDays: '' })
                  }
                }}
              />
              {formData.productionTimeInDays && (
                <span className="absolute right-3 top-[32px] text-muted-foreground text-sm pointer-events-none">
                  days
                </span>
              )}
            </div>
          </div>
          
          <TextArea
            label="Product details"
            placeholder="Product details"
            value={formData.details}
            className='bg-muted rounded-[10px] h-[146px] w-full'
            onChange={(e) => setFormData({ ...formData, details: e.target.value })}
          />

          {/* PE-1283 §7 E4 — product-level media gallery (images + transcoded video) */}
          <ProductMediaGallery
            items={formData.media}
            onChange={(media) => setFormData({ ...formData, media })}
          />
        </div>

        {/* Product List Table - Only show if base data is valid */}
        {canAddVariants() && (
          <div className="mt-8 space-y-6 border-[1px] border-[#B8B5B6] rounded-[25px] p-7 bg-card h-80">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-[600] text-[]">Product List</h2>
            </div>

            {propertyData.sizes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-[1px] border-[#B8B5B6] pb-5">
                      <th className="text-left py-3 pr-4 font-[600] text-[18px]">Material</th>
                      <th className="text-left py-3 px-4 font-[600] text-[18px]">Color</th>
                      {getAvailableSizes().map(size => (
                        <th key={size.id} className="text-center py-3 px-2 font-[600] text-[18px] min-w-[60px]">{size.name}</th>
                      ))}
                      <th className="text-left py-3 px-4 font-[600] text-[18px]">Images</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      interface TableRow {
                        material: string
                        color: { name: string; hex: string }
                        size: string
                        quantity: number
                        images: (File | string)[]
                      }

                      interface GroupedRow {
                        material: string
                        color: { name: string; hex: string }
                        sizes: { [key: string]: number }
                        images: (File | string)[]
                      }

                      const rows: TableRow[] = []
                      propertyData.sizes.forEach(size => {
                        size.materials.forEach(material => {
                          material.colors.forEach(color => {
                            const colorName = colorOptions.find(c => c.value === color.hex)?.name || 'Unknown'
                            rows.push({
                              material: material.name,
                              color: { name: colorName, hex: color.hex },
                              size: size.sizeId,
                              quantity: color.quantity,
                              images: [...(color.imageUrls || []), ...(color.images || [])]
                            })
                          })
                        })
                      })

                      // Group by material and color
                      const grouped: { [key: string]: GroupedRow } = {}
                      rows.forEach(row => {
                        const key = `${row.material}-${row.color.hex}`
                        if (!grouped[key]) {
                          grouped[key] = {
                            material: row.material,
                            color: row.color,
                            sizes: {},
                            images: row.images
                          }
                        }
                        grouped[key].sizes[row.size] = row.quantity
                      })

                      return Object.values(grouped).map((row: GroupedRow, index: number) => (
                        <tr key={index} className="border-b border-border hover:bg-muted py-5 c">
                          <td className="py-3 px-4 text-[16px] text-foreground" >{row.material}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full border"
                                style={{ backgroundColor: row.color.hex }}
                              />
                              <span className="text-[16px] text-foreground">{row.color.name}</span>
                            </div>
                          </td>
                          {getAvailableSizes().map(size => (
                            <td key={size.id} className="py-3 px-2 text-center text-[16px] text-foreground">
                              {row.sizes[size.id] || '-'}
                            </td>
                          ))}
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              {row.images.slice(0, 2).map((image, idx) => (
                                <img
                                  key={idx}
                                  src={typeof image === 'string' ? image : URL.createObjectURL(image)}
                                  alt={`${row.color.name} ${idx + 1}`}
                                  className="w-[51px] h-[45px] border-[0.44px]  border-[#B8B5B6] object-cover rounded-[4.44px]"
                                />
                              ))}
                              {row.images.length > 2 && (
                                <span className="text-[12px] text-muted-foreground ml-1">+{row.images.length - 2}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    })()
                    }
                  </tbody>
                </table>
                <div className="flex justify-end mt-4 ">
                  <Button variant="outline" onClick={handlePropertiesModal} size="sm" className='w-[150px]'>
                    Edit
                  </Button>
                </div>
              </div>
            ) : (
                <div className='flex justify-center items-center'>
                  {propertyData.sizes.length === 0 && (
                    <div className="flex justify-center mt-20 item-center">
                      <Button
                        variant="outline"
                        onClick={addProperty}
                        size='sm'
                        disabled={!canAddVariants()}
                        icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3.33334V12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M3.33334 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>}
                        iconPosition="left"
                      >
                        Add properties
                      </Button>
                    </div>
                  )}
                </div>
              )}
          </div>
        )}

        {/* Message when base data is not complete */}
        {!canAddVariants() && (
          <div className="mt-8 p-6 bg-muted rounded-lg text-center">
            <p className="text-muted-foreground">Complete the basic product information above to add variants</p>
          </div>
        )}

        <PropertiesModal
          isOpen={showPropertiesModal}
          onClose={() => setShowPropertiesModal(false)}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          propertyData={propertyData}
          setPropertyData={setPropertyData}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          selectedMaterial={selectedMaterial}
          setSelectedMaterial={setSelectedMaterial}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
          getAvailableSizes={getAvailableSizes}
          getAvailableMaterials={getAvailableMaterials}
          getAvailableColors={getAvailableColors}
          getSizeCostPrice={getSizeCostPrice}
          getSizePrice={getSizePrice}
          getMaterialCostPrice={getMaterialCostPrice}
          getMaterialPrice={getMaterialPrice}
          getColorCostPrice={getColorCostPrice}
          getColorPrice={getColorPrice}
          // hasMaterialPrices={hasMaterialPrices}
          getCurrentQuantity={getCurrentQuantity}
          getCurrentImages={getCurrentImages}
          getCurrentWeight={getCurrentWeight}
          handleSizeCostPriceChange={handleSizeCostPriceChange}
          handleSizePriceChange={handleSizePriceChange}
          handleMaterialCostPriceChange={handleMaterialCostPriceChange}
          handleMaterialPriceChange={handleMaterialPriceChange}
          handleColorCostPriceChange={handleColorCostPriceChange}
          handleColorPriceChange={handleColorPriceChange}
          handleQuantityChange={handleQuantityChange}
          handleWeightChange={handleWeightChange}
          handleImageChange={handleImageChange}
          handleImageDelete={handleImageDelete}
          materials={materials}
          colorOptions={colorOptions}
        />
      </div>
    </DropdownProvider>
  )
}